/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable:max-line-length */
/* tslint:disable:cyclomatic-complexity */

import { ILoyaltySignUpViewProps } from '@msdyn365-commerce-modules/account-management';
import {
    issueLoyalty,
    IssueLoyaltyInput,
    updateCustomerAttributes,
    updateCustomerAttributesInput
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    Address,
    AttributeDefinition,
    CustomerAttribute,
    LoyaltyGroup
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import MaskInput from 'react-maskinput';
import {
    IHeadingData,
    ILoyaltySignUpProps,
    ILoyaltySignUpResources,
    ISimpleMarketingConsentAttributesData,
    ITermsAndConditionsLinkData
} from '../definition-extensions/loyalty-sign-up.ext.props.autogenerated';
import { ILoyaltySignUpData } from './loyalty-sign-up.data';

const LoyaltySignUpView: React.FC<ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>> = props => {
    const {
        LoyaltySignUp,
        config: { termsAndConditionsLink },
        loading,
        slots: { dateOfBirthText }
    } = props;
    if (loading) {
        props.context.telemetry.error(props.resources.moduleWontRenderErrorText);
        return null;
    }
    const attributeDefinition: AttributeDefinition[] = (props.data.attributeDefinitions && props.data.attributeDefinitions.result) || [];
    const [mask, setMask] = useState('00/00/0000');
    const [maskString, setMaskString] = useState('DD/MM/YYYY');
    const [dateOfBirth, setdateOfBirth] = useState(Object);
    const [localStore, setLocalStore] = useState(Object);
    const [title, setTitle] = useState(Object);
    const [gender, setGender] = useState(Object);
    const [mobileNumber, setMobileNumber] = useState(Object);
    const [promoCode, setPromoCode] = useState('');
    const [isEmailChecked, setIsEmailChecked] = useState(Object);
    const [isPostChecked, setIsPostChecked] = useState(Object);
    const [isResearchChecked, setIsResearchChecked] = useState(Object);
    const [isSmsChecked, setIsSmsChecked] = useState(Object);
    const [isProfilingChecked, setIsProfilingChecked] = useState(Object);
    const [day, setDay] = useState(Object);
    const [month, setMonth] = useState(Object);
    const [year, setYear] = useState(Object);
    const [disabled, setDisabled] = useState(true);
    const [error, SetError] = useState('');
    const [displayNotification, SetDisplayNotification] = useState(false);
    const [overEighteen, SetOverEighteen] = useState(true);
    const [isValidDob, SetIsValidDob] = useState(false);
    const [isYearOutOfRange, setIsYearOutOfRange] = useState(false);
    const [minYear, SetMinYear] = useState(-1);
    const [maxYear, SetMaxYear] = useState(-1);
    const [dateError, SetDateError] = useState(''); // This state is used to display dob errors while adding dob
    const [isAddressError, setIsAddressError] = useState(false); // Will be true if there is no address
    const [addressError, setAddressError] = useState(''); // Error message for address
    const [inputCheck, setInputCheck] = useState([
        {
            Name: 'Contactable Email',
            BooleanValue: false
        },
        {
            Name: 'Contactable Post',
            BooleanValue: false
        },
        {
            Name: 'Contactable Mobile',
            BooleanValue: false
        }
    ]);

    const { loyaltyCard, customerInformation } = props.data;
    const loyaltyGroup = loyaltyCard.result?.LoyaltyGroups && loyaltyCard.result?.LoyaltyGroups[0];
    const query = props.context.request.query;
    const dobbiesClubSiteUrl = props.context.request.app.config.dobbiesClubSiteUrl;
    const patternAttribute = {
        pattern: `^\s*\(?(020[7,8]{1}\)?[ ]?[1-9]{1}[0-9{2}[ ]?[0-9]{4})|(0[1-8]{1}[0-9]{3}\)?[ ]?[1-9]{1}[0-9]{2}[ ]?[0-9]{3})\s*$`
    };

    if (!props.context.request.user.isAuthenticated) {
        const signinLink = props.context.request.user.signInUrl;
        window.location.assign(signinLink!);
    }

    useEffect(() => {
        const incomingMarketingAttributes = customerInformation.result?.Attributes;
        if (incomingMarketingAttributes!.length > 0) {
            incomingMarketingAttributes?.map(attr => {
                if (attr.Name === 'Contactable Post') {
                    setIsPostChecked(setAttributeValue(attr.AttributeValue?.BooleanValue!, attr, true));
                    setInputCheck(prevData =>
                        prevData.map(item =>
                            item.Name === attr.Name ? { ...item, BooleanValue: attr.AttributeValue?.BooleanValue! } : item
                        )
                    );
                }
                if (attr.Name === 'Contactable Email') {
                    setIsEmailChecked(setAttributeValue(attr.AttributeValue?.BooleanValue!, attr, true));
                    setInputCheck(prevData =>
                        prevData.map(item =>
                            item.Name === attr.Name ? { ...item, BooleanValue: attr.AttributeValue?.BooleanValue! } : item
                        )
                    );
                }
                if (attr.Name === 'Contactable Mobile') {
                    setIsSmsChecked(setAttributeValue(attr.AttributeValue?.BooleanValue!, attr, true));
                    setInputCheck(prevData =>
                        prevData.map(item =>
                            item.Name === attr.Name ? { ...item, BooleanValue: attr.AttributeValue?.BooleanValue! } : item
                        )
                    );
                }
            });
        }
    }, []);

    const getTier = (props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>) => {
        const loyaltyGroup = props.data.loyaltyCard.result?.LoyaltyGroups && props.data.loyaltyCard.result?.LoyaltyGroups[0];
        // highestLoyaltCardTier
        const highestActiveLoyaltyCardTier = loyaltyGroup && loyaltyGroup.HighestActiveLoyaltyCardTier;
        const loyaltyCardTiers = loyaltyGroup && loyaltyGroup.LoyaltyCardTiers;
        // Get TierId based on the active cardTier
        if (loyaltyCardTiers && loyaltyCardTiers?.length > 0) {
            for (const [_, loyaltyCardTier] of loyaltyCardTiers.entries()) {
                if (loyaltyCardTier.RecordId === highestActiveLoyaltyCardTier) {
                    return loyaltyCardTier;
                }
            }
        }
        return;
    };

    const tier = getTier(props);

    if (tier && tier.TierId !== 'Club') {
        // Redirect to club-plus,  if user has membership of club plus or club plus free trial
        const today = new Date();
        const validTill = new Date(props.data.loyaltyCard.result?.LoyaltyGroups![0].LoyaltyCardTiers![0].ValidFrom!);
        const expiryDate = new Date(today);
        const validNumberOfDays: number = props.app.config.CheckValidityForTheNumberOfDays;
        expiryDate.setDate(expiryDate.getDate() + validNumberOfDays);

        // To calculate the time difference of two dates
        const differenceInTime = expiryDate.getTime() - validTill.getTime();

        // To calculate the no. of days between two dates
        const differenceInDays = differenceInTime / (1000 * 3600 * 24);
        console.log('diff', differenceInDays, differenceInDays <= 0);

        if (
            query &&
            query.tier &&
            query.tier.toLowerCase() === 'club-plus-trial' &&
            tier &&
            (tier.TierId === 'Club Plus' || tier.TierId === 'Club Plus Free Tier')
        ) {
            window.location.assign(`${dobbiesClubSiteUrl}?tier=club-plus`);
        }
    }

    // UseEffect hook to set minyear and maxYear into state (useState hooks)
    setMinAndMaxYear(props, SetMinYear, minYear, SetMaxYear, maxYear);
    // Custom hook to get the customer address count.
    const addressCount = useGetAddressesCount(props);
    useEffect(() => {
        if (addressCount === undefined || addressCount?.length === 0) {
            setIsAddressError(true);
        } else {
            setIsAddressError(false);
            setAddressError('');
        }
    });

    // Get TierId based on the active cardTier Here's the tier ID could be (club, club plus)
    const tierId = getTierId(loyaltyGroup);
    /* VSI-Customization -- Redirect user based on tier conditions  */
    redirectUserIfTierExist(props, tierId);

    const loyaltyProgramText =
        termsAndConditionsLink &&
        _renderTermsAndConditionsText(
            termsAndConditionsLink,
            props.resources.loyaltyProgramText,
            '__term-and-conditions-link',
            '__terms-and-conditions-text'
        );
    const { loyaltyTermsLinkText, dynamicClass } = getDynamicClass(query, props);

    // tslint:disable-next-line:no-any
    const updateDOB = (e: any): void => {
        const {
            resources: { validYearErrorText, ageRestrictionError, validDateErrorText }
        } = props;
        let isInvalid = false;
        let ageRestriction = false;
        let invalidYear = false;
        let futureDate = false; // If user enters dob greater than today
        const dob = e && e.target && e.target.value;
        if (parseInt(dob[6], 10) > 2) {
            setMaskString('DD/MM/YY');
            setMask('00/00/00');
        } else {
            setMaskString('DD/MM/YYYY');
            setMask('00/00/0000');
        }
        if (moment(dob, 'DD/MM/YYYY').format('DD/MM/YYYY') !== 'Invalid date') {
            const inputDob = moment(dob, 'DD/MM/YYYY', true);
            const isValidInput = inputDob.isValid();
            const inputDate = moment(dob, 'DD/MM/YYYY').format('DD/MM/YYYY');
            const today = new Date();
            const currentYear = today.getFullYear();
            const dobYear = moment(dob, 'DD/MM/YYYY').year();
            if (isValidInput) {
                const years = moment(inputDate, 'DD/MM/YYYY').fromNow(true); // Years difference from today
                if (inputDob < moment()) {
                    SetIsValidDob(true);
                    const diffYears = years.split(' ');
                    const yearLabel = diffYears[1].trim().toLowerCase();
                    if (yearLabel === 'year' || yearLabel === 'years') {
                        const _difference = diffYears[0].trim().toLowerCase();
                        const difference = parseInt(_difference, 10);
                        if ((dobYear < currentYear && _difference === 'a') || difference < 18) {
                            // Difference is 'a year' in case of 1 year and x months in case of some months
                            SetOverEighteen(false);
                            SetDisplayNotification(true);
                            ageRestriction = true;
                        } else if (dobYear < minYear) {
                            // dob must not be less than 1990
                            SetDisplayNotification(true);
                            setIsYearOutOfRange(true);
                            invalidYear = true;
                        } else {
                            SetOverEighteen(true);
                            SetDisplayNotification(false);
                            SetDateError(''); // Remove date error if any
                        }
                    } else if (dobYear < currentYear) {
                        // when dobyear is less and difference is just of months or days
                        SetOverEighteen(false);
                        SetDisplayNotification(true);
                        setIsYearOutOfRange(false);
                        ageRestriction = true;
                    } else {
                        futureDate = true; // input date is greater than today
                    }
                } else {
                    futureDate = true; // input date is greater than today
                }
            } else {
                // date is not valid
                isInvalid = true;
            }
            SetError('');
            setdateOfBirth(setAttributeValue(moment(dob, 'DD/MM/YYYY').format('DD/MM/YYYY'), dateOfBirth, false));
            updateDayMonthYearAttributes(moment(dob, 'DD/MM/YYYY').format('DD/MM/YYYY'));
        } else {
            setdateOfBirth((prevState: CustomerAttribute) => ({
                ...prevState,
                AttributeValue: {
                    // @ts-ignore
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CommercePropertyValue',
                    StringValue: ''
                }
            }));
            isInvalid = true; // date is not valid
        }
        if (isInvalid) {
            SetDateError(validDateErrorText);
            SetIsValidDob(false);
            setIsYearOutOfRange(false);
            SetDisplayNotification(true);
        } else if (futureDate) {
            SetIsValidDob(true);
            setIsYearOutOfRange(true);
        }
        let invalidDate = false; // Following code is to check date erorr while entering dob
        if (ageRestriction) {
            SetError(ageRestrictionError);
            invalidDate = true;
        } else if (invalidYear || futureDate) {
            SetError(validYearErrorText);
            invalidDate = true;
        }
        if (invalidDate) {
            SetDateError(validYearErrorText);
        }
    };

    const setAttributeDefaultValues = () => {
        const {
            genderAttribute,
            titleAttribute,
            contactableEmailAttribute,
            contactableMobileAttribute,
            contactablePostAttribute,
            mobileAttribute
        } = props.config;
        const {
            contactableProfilingAttribute,
            contactableResearchAttribute,
            dayAttribute,
            monthAttribute,
            yearAttribute,
            localStoreAttribute
        } = props.config;
        // tslint:disable-next-line: cyclomatic-complexity
        attributeDefinition?.forEach(attr => {
            const Name = attr.Name?.toLowerCase();
            if (Name === localStoreAttribute.toLowerCase() && Object.keys(localStore).length === 0) {
                setLocalStore(setAttrDefaultValues(attr));
            }
            if (Name === genderAttribute.toLowerCase() && Object.keys(gender).length === 0) {
                setGender(setAttrDefaultValues(attr));
            }
            if (Name === titleAttribute.toLowerCase() && Object.keys(title).length === 0) {
                setTitle(setAttrDefaultValues(attr));
            }
            if (Name === contactableEmailAttribute.toLowerCase() && Object.keys(isEmailChecked).length === 0) {
                setIsEmailChecked(setAttrDefaultValues(attr));
            }
            if (Name === contactablePostAttribute.toLowerCase() && Object.keys(isPostChecked).length === 0) {
                setIsPostChecked(setAttrDefaultValues(attr));
            }
            if (Name === contactableMobileAttribute.toLowerCase() && Object.keys(isSmsChecked).length === 0) {
                setIsSmsChecked(setAttrDefaultValues(attr));
            }
            if (Name === contactableResearchAttribute.toLowerCase() && Object.keys(isResearchChecked).length === 0) {
                setIsResearchChecked(setAttrDefaultValues(attr));
            }
            if (Name === contactableProfilingAttribute.toLowerCase() && Object.keys(isProfilingChecked).length === 0) {
                setIsProfilingChecked(setAttrDefaultValues(attr));
            }
            if (Name === mobileAttribute.toLowerCase() && Object.keys(mobileNumber).length === 0) {
                setMobileNumber(setAttrDefaultValues(attr));
            }
            if (Name === dayAttribute.toLowerCase() && Object.keys(day).length === 0) {
                setDay(setAttrDefaultValues(attr));
            }
            if (Name === monthAttribute.toLowerCase() && Object.keys(month).length === 0) {
                setMonth(setAttrDefaultValues(attr));
            }
            if (Name === yearAttribute.toLowerCase() && Object.keys(year).length === 0) {
                setYear(setAttrDefaultValues(attr));
            }
        });
    };
    // Run at initial render and set the attribute values of customer
    setAttributeDefaultValues();

    const handleClubPlusScenario = async () => {
        // Check if address existed or not
        const isValid = validateAttributeValuesForClubPlus();
        if (isValid) {
            /* -3 If user is dobbies club free member and wants to enroll for club plus membership redirects him to checkout page */
            const {
                clubFreeTierText,
                directDebitAttribute: directDebitAttr,
                directDebitAttributeValue,
                promoCodeAttribute: promoCodeAttr
            } = props.config;
            if (
                (loyaltyCard.result?.CardNumber &&
                    tierId?.toLowerCase() === clubFreeTierText?.toLowerCase() &&
                    query?.tier?.toLowerCase() === 'club-plus') ||
                query?.tier?.toLowerCase() === 'club-plus-trial'
            ) {
                // update attribute Direct Debit
                const customer = props.data.customerInformation.result || { Attributes: [], AccountNumber: '' };
                const directDebitAttribute = getCustomAttribute(props, directDebitAttr, directDebitAttributeValue);
                const promoCodeAttribute = getCustomAttribute(props, promoCodeAttr, promoCode);
                // If user previously entered empty promo code filter the array and add newly promo code attribute with udpated value.
                const filteredAttributes = customer.Attributes?.filter(x => x.Name?.toLowerCase() !== promoCodeAttr.toLowerCase());
                if (customer && directDebitAttribute && filteredAttributes) {
                    filteredAttributes.push(directDebitAttribute);
                    filteredAttributes.push(promoCodeAttribute);
                }
                const input = new updateCustomerAttributesInput(
                    customer.AccountNumber,
                    filteredAttributes || [],
                    props.context.request.apiSettings
                );
                updateCustomerAttributes(input, props.context.actionContext).then(() => {
                    if (query && query.tier && query.tier.toLowerCase() === 'club-plus-trial') {
                        window.location.assign(`${dobbiesClubSiteUrl}?tier=club-plus-trial`);
                    } else {
                        window.location.assign(`${dobbiesClubSiteUrl}?tier=club-plus`);
                    }
                    return;
                });
            }
            // If cardNumber doesn't exist or Tier is expired it means user wants to enroll for membership
            if (!loyaltyCard.result?.CardNumber) {
                _issueLoyalty();
            }
        }
        return;
    };
    // validate attribute values and update state accordingly
    const validateAttributeValuesForClubPlus = () => {
        const {
            resources: { addressRequiredError }
        } = props;
        if (isAddressError) {
            SetError(addressRequiredError);
            setAddressError(props.resources.addressRequiredError);
            setDisabled(false);
            return false;
        }
        return true;
    };
    // validate attribute values and update state accordingly
    const validateAttributeValues = () => {
        const {
            resources: {
                validDateErrorText,
                validYearErrorText,
                ageRestrictionError,
                validGenderErrorText,
                titleErrorMessage,
                addressRequiredError
            }
        } = props;
        if (!isValidDob) {
            SetError(validDateErrorText);
            SetDisplayNotification(true);
            setDisabled(false);
            return;
        }
        if (isYearOutOfRange) {
            SetError(validYearErrorText);
            setDisabled(false);
            return;
        }
        if ((dateOfBirth && dateOfBirth.AttributeValue.StringValue === '') || !isValidDob) {
            SetError(validDateErrorText);
            setDisabled(false);
            return;
        }
        if (!overEighteen) {
            SetError(ageRestrictionError);
            setDisabled(false);
            return;
        }
        if (gender && gender.AttributeValue && gender.AttributeValue.StringValue === '') {
            SetError(validGenderErrorText);
            setDisabled(false);
            return;
        }
        // If localStore default value is set to empty string then set it to default None
        if (localStore && localStore.AttributeValue && localStore.AttributeValue.StringValue === '') {
            localStore.AttributeValue.StringValue = 'None';
        }
        if (title && title.AttributeValue && title.AttributeValue.StringValue === '') {
            SetError(titleErrorMessage);
            setDisabled(false);
            return;
        }
        if (isAddressError) {
            SetError(addressRequiredError);
            setDisabled(false);
            return;
        }
        return true;
    };
    // When user wants to enroll for loyalty membership below function will triggered
    const updateAttributes = async () => {
        const CardNumber = props.data.loyaltyCard.result?.CardNumber;
        const {
            context,
            config: { promoCodeAttribute }
        } = props;
        if (!props.context.request.user.isAuthenticated) {
            return;
        }
        // If cardNumber doesn't exist then validate attributes
        if (!CardNumber) {
            const isValid = validateAttributeValues();
            if (!isValid) {
                return;
            }
            setDisabled(true);
            const _customer = props.data.customerInformation.result || { Attributes: [], AccountNumber: '' };
            const attributes = [];
            attributes.push(gender);
            attributes.push(day);
            attributes.push(month);
            attributes.push(year);
            attributes.push(localStore);
            attributes.push(title);
            attributes.push(isSmsChecked);
            attributes.push(isResearchChecked);
            attributes.push(isEmailChecked);
            attributes.push(isPostChecked);
            attributes.push(isProfilingChecked);
            attributes.push(mobileNumber);
            // If user doesn't have cardNumber and query param is 'club-plus' send the direct debit attribute
            if (
                context.request.query?.tier?.toLowerCase() === 'club-plus' ||
                context.request.query?.tier?.toLowerCase() === 'club-plus-trial'
            ) {
                attributes.push(getCustomAttribute(props, props.config.directDebitAttribute, props.config.directDebitAttributeValue));
                attributes.push(getCustomAttribute(props, promoCodeAttribute, promoCode));
            }
            if (_customer) {
                _customer.Attributes = attributes;
            }
            // If cardNumber doesn't exist it means we've to set attributes
            const input = new updateCustomerAttributesInput(
                _customer.AccountNumber,
                _customer.Attributes || [],
                context.request.apiSettings
            );
            updateCustomerAttributes(input, context.actionContext)
                .then(async () => {
                    // Here we need to add several checks
                    await handleClubPlusScenario();
                })
                .catch((err: Error) => {
                    setDisabled(false);
                });
        } else {
            await handleClubPlusScenario();
        }
    };

    const _issueLoyalty = () => {
        if (props.context.request.user.isAuthenticated) {
            const input = new IssueLoyaltyInput(props.context.request.apiSettings, props.context.request.user.customerAccountNumber);
            issueLoyalty(input, props.context.actionContext)
                .then(() => {
                    if (query && query.tier && query.tier.toLowerCase() === 'club-free') {
                        window.location.assign(Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '');
                    } else if (query && query.tier && query.tier.toLowerCase() === 'club-plus-trial') {
                        window.location.assign(`${dobbiesClubSiteUrl}?tier=club-plus-trial`);
                    } else {
                        window.location.assign(`${dobbiesClubSiteUrl}?tier=club-plus`);
                    }
                })
                .catch((err: Error) => {
                    props.telemetry.error(err.message);
                    props.telemetry.debug(props.resources.loyaltyCardIssue);
                    setDisabled(false);
                });
        }
    };

    const updateGenderValue = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedGender = event.target.value;
        SetError('');
        setGender(setAttributeValue(selectedGender, gender, false));
    };
    const updateLocalStore = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedLocalStore = event.target.value;
        SetError('');
        setLocalStore(setAttributeValue(selectedLocalStore, localStore, false));
    };
    const updateTitle = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedTitle = event.target.value;
        SetError('');
        setTitle(setAttributeValue(selectedTitle, title, false));
    };
    const updateDayMonthYearAttributes = (dob: string) => {
        const [D, M, Y] = dob.split('/');
        setDay(setAttributeValue(D, day, false));
        setMonth(setAttributeValue(M, month, false));
        setYear(setAttributeValue(Y, year, false));
    };
    /** VSI Customization -- END */
    const renderDropDownOptionsForAttribute = (attribute: string) => {
        const attr = props.data.attributeDefinitions?.result?.find(x => x.AttributeTypeName?.toLowerCase() === attribute.toLowerCase());
        return attr?.EnumerationDetails?.map(e => {
            return (
                <option aria-selected='true' value={e.EnumerationValue} key={e.EnumerationValue}>
                    {e.EnumerationValue}
                </option>
            );
        });
    };
    // Render dropdown options for localstore attribute
    const renderLocalStores = () => {
        const { localStoreAttribute } = props.config;
        const localStoreAttr = props.data.attributeDefinitions?.result?.find(
            x => x.AttributeTypeName?.toLowerCase() === localStoreAttribute.toLowerCase()
        );
        const localStores = localStoreAttr?.EnumerationDetails?.sort((a, b) => a.EnumerationValue!.localeCompare(b.EnumerationValue!));
        // Move none value to the start of array
        const noneVal =
            localStores &&
            localStores.find(store => {
                return store.EnumerationValue && store.EnumerationValue.toLowerCase() === 'none';
            });
        localStores?.sort((x, y) => {
            return x.EnumerationValue === noneVal?.EnumerationValue ? -1 : y === noneVal?.EnumerationValue ? 1 : 0;
        });
        return localStores?.map(e => {
            return (
                <option aria-selected='true' value={e.EnumerationValue} key={e.EnumerationValue}>
                    {e.EnumerationValue}
                </option>
            );
        });
    };

    /* VSI-Customization -- Update button attribute disabled (true or false) based on certain checkbox checked or not  */
    const handleTermsCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        // update state (toggle) based on terms and conditions checkbox checked or not
        setDisabled(!e.target.checked);
    };
    /* VSI Customization -- Check if gender is not selected, display error message */
    const _genderNotSelectedErrorMsg = () => {
        const { validGenderErrorText } = props.resources;
        // Dont display error on adding dob input i.e. before clicking Next button
        if (dateError !== '') {
            return;
        }
        if (error !== '' && gender && gender.AttributeValue && gender.AttributeValue.StringValue === '') {
            return <Node className='gender-not-selected-error'>{validGenderErrorText}</Node>;
        }
        return;
    };
    /* VSI Customization -- Check if title is not selected, display error message */
    const titleNotSelectedErrorMsg = () => {
        const { titleErrorMessage } = props.resources;
        // Dont display error on adding dob input i.e. before clicking Next button
        if (dateError !== '') {
            return;
        }
        if (error !== '' && title && title.AttributeValue && title.AttributeValue.StringValue === '') {
            return <Node className='localstore-not-selected-error'>{titleErrorMessage}</Node>;
        }
        return;
    };
    // Render loyalty terms link
    const _renderLoyaltyTermsLink = () => {
        const termsUrl = Msdyn365.getUrlSync('club-terms-and-conditions', props.context.actionContext);
        return (
            <Button className='ms-loyalty-signup__terms' href={termsUrl}>
                {loyaltyTermsLinkText}
            </Button>
        );
    };
    // event handler for mobile field
    const handleMobileNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setMobileNumber(setAttributeValue(value, mobileNumber, false));
    };
    // Event handler for marketing consent attributes
    const handleMarketingConsent = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {
            contactableEmailAttribute,
            contactableMobileAttribute,
            contactablePostAttribute,
            contactableProfilingAttribute,
            contactableResearchAttribute
        } = props.config;
        const targetName = event.target.name.toLowerCase();
        const isChecked = event.target.checked;
        if (targetName === contactablePostAttribute.toLowerCase()) {
            setIsPostChecked(setAttributeValue(isChecked, isPostChecked, true));
            setInputCheck(prevData =>
                prevData.map(item => (item.Name.toLowerCase() === targetName ? { ...item, BooleanValue: isChecked } : item))
            );
        }
        if (targetName === contactableEmailAttribute.toLowerCase()) {
            setIsEmailChecked(setAttributeValue(isChecked, isEmailChecked, true));
            setInputCheck(prevData =>
                prevData.map(item => (item.Name.toLowerCase() === targetName ? { ...item, BooleanValue: isChecked } : item))
            );
        }
        if (targetName === contactableResearchAttribute.toLowerCase()) {
            setIsResearchChecked(setAttributeValue(isChecked, isResearchChecked, true));
        }
        if (targetName === contactableMobileAttribute.toLowerCase()) {
            setIsSmsChecked(setAttributeValue(isChecked, isSmsChecked, true));
            setInputCheck(prevData =>
                prevData.map(item => (item.Name.toLowerCase() === targetName ? { ...item, BooleanValue: isChecked } : item))
            );
        }
        if (targetName === contactableProfilingAttribute.toLowerCase()) {
            setIsProfilingChecked(setAttributeValue(isChecked, isProfilingChecked, true));
        }
    };
    // Render Marketing consent attributes section
    const renderMarketingConsentSection = () => {
        const cardNumber = props.data.loyaltyCard.result?.CardNumber;
        const {
            marketingPreferenceHeading,
            clubMarketingConsentDescription,
            clubPlusMarketingConsentDescription,
            updatePreferenceText
        } = props.resources;
        const { clubPlusMarketingConsentAttributes, simpleMarketingConsentAttributes } = props.config;
        if (!cardNumber) {
            return (
                <>
                    <div className='loyalty-signup-section contact-permission-section'>
                        <div>
                            <h2>{marketingPreferenceHeading}</h2>
                            <p>{clubMarketingConsentDescription}</p>
                            <div className='ms-loyalty-signup__marketing-consents'>
                                {/* Render club marketing consent attributes section */}
                                <div className='ms-loyalty-signup__marketing-consents--section'>
                                    {simpleMarketingConsentAttributes && renderMarketingConsentAttributes(simpleMarketingConsentAttributes)}
                                </div>
                                <p>{clubPlusMarketingConsentDescription}</p>
                                <div className='ms-loyalty-signup__marketing-consents'>
                                    {/* Render club plus marketing consent attributes section */}
                                    <div className='ms-loyalty-signup__marketing-consents--section'>
                                        {clubPlusMarketingConsentAttributes &&
                                            renderMarketingConsentAttributes(clubPlusMarketingConsentAttributes)}
                                    </div>
                                </div>
                                <p>{updatePreferenceText}</p>
                            </div>
                        </div>
                    </div>
                    {props.signInLink}
                </>
            );
        }
        return;
    };
    // Render checkboxes for marketing consent attributes
    const renderMarketingConsentAttributes = (marketingConsentAttributes: ISimpleMarketingConsentAttributesData[]) => {
        return marketingConsentAttributes.map(attr => {
            const { attributeLabel, attributeName } = attr;
            const filteredInputCheck = inputCheck.find(attr2 => attr2.Name === attributeName && attr2.BooleanValue);
            if (filteredInputCheck) {
                return (
                    <span key={attributeLabel} className='checkbox-section'>
                        <input
                            type='checkbox'
                            aria-checked='true'
                            checked={filteredInputCheck ? filteredInputCheck.BooleanValue : false}
                            id={attributeName}
                            name={attributeName}
                            onChange={handleMarketingConsent}
                        />
                        <label htmlFor={attributeName}>{attributeLabel}</label>
                    </span>
                );
            } else {
                return (
                    <span key={attributeLabel} className='checkbox-section'>
                        <input
                            type='checkbox'
                            aria-checked='true'
                            id={attributeName}
                            name={attributeName}
                            onChange={handleMarketingConsent}
                        />
                        <label htmlFor={attributeName}>{attributeLabel}</label>
                    </span>
                );
            }
        });
    };
    // Render date of birth field and display error message if occurs
    const renderDateOfBirthField = () => {
        const { dateOfBirthLabel } = props.resources;
        return (
            <div className='loyalty-signup-section__input-holder'>
                <form>
                    <label>{dateOfBirthLabel}</label>
                    <MaskInput alwaysShowMask maskString={maskString} mask={mask} onChange={updateDOB} />
                    {/* slot will be rendered for dob description text */}
                    {dateOfBirthText && dateOfBirthText}
                </form>
                {_invalidDobErrorMsg(
                    displayNotification,
                    error,
                    dateError,
                    isValidDob,
                    overEighteen,
                    props.resources,
                    isYearOutOfRange,
                    minYear,
                    maxYear
                )}
            </div>
        );
    };
    // Render local store select element
    const renderLocalStoreField = () => {
        const { storeLabel } = props.resources;
        return (
            <div className='loyalty-signup-section__input-holder'>
                <label htmlFor='store'>{storeLabel}</label>
                <div className='select-holder'>
                    <select name='store' onChange={updateLocalStore}>
                        {renderLocalStores()}
                    </select>
                </div>
            </div>
        );
    };
    // Render mobile field
    const renderMobileField = () => {
        const { mobileLabel } = props.resources;
        return (
            <div className='loyalty-signup-section__input-holder'>
                <label>{mobileLabel}</label>
                <input
                    type='number'
                    onChange={handleMobileNumber}
                    aria-valuemax={12}
                    aria-valuemin={11}
                    aria-valuenow={0}
                    name='mobile'
                    {...patternAttribute}
                />
            </div>
        );
    };
    // Render select element for given attribute
    const renderSelectElement = (
        label: string,
        onChangeHandler: React.ChangeEventHandler<HTMLSelectElement>,
        dropdownLabel: string,
        attribute: string
    ) => {
        return (
            <div className='loyalty-signup-section__input-holder'>
                <label>{label}</label>
                <div className='select-holder'>
                    <select name={attribute} onChange={onChangeHandler}>
                        <option aria-selected='true' value=''>
                            {dropdownLabel}
                        </option>
                        {renderDropDownOptionsForAttribute(attribute)}
                    </select>
                </div>
            </div>
        );
    };

    const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPromoCode(e.currentTarget.value);
    };
    const renderPromoCodeField = () => {
        const {
            resources: { promoCodeLabel }
        } = props;
        // const cardNumber = props.data.loyaltyCard.result?.CardNumber;
        // if (cardNumber || (!cardNumber && query?.tier?.toLowerCase() === 'club-plus')) {
        return (
            <div className='loyalty-signup-section__input-holder'>
                <label>{promoCodeLabel}</label>
                <input type='text' onChange={handlePromoCodeChange} />
            </div>
        );
        // }
        // return;
    };

    // Render Loyalty terms and conditions section with Action button
    const renderLoyaltyTermsAndConditionsSection = () => {
        const { joinLoyaltyButtonText } = props.resources;
        return (
            <div className='loyalty-signup-section'>
                <div className='loyalty-signup-section__title'>{renderHeading(props.config.heading)}</div>
                <div className='loyalty-signup-section__input-holder'>
                    <p className='ms-loyalty-signup__text'>{loyaltyProgramText}</p>
                </div>
                <div className='loyalty-signup-section__checkbox-holder'>
                    <input onChange={handleTermsCheck} aria-checked='mixed' type='checkbox' name='agree' id='agree' className='checkbox' />
                    {_renderLoyaltyTermsLink()}
                </div>
                {/* To handle click functionality overriding the loyalty sign up button */}
                {
                    <Button className='ms-loyalty-signup__btn' disabled={disabled} onClick={updateAttributes}>
                        {joinLoyaltyButtonText}
                    </Button>
                }
            </div>
        );
    };
    // Render full view for loyalty sign up page
    const renderLoyaltySignUpView = () => {
        const { titleLabel, pleaseSelectText, genderLabel } = props.resources;
        const { nonClubMemberStatement, memberStatement } = props.config;
        const { titleAttribute, genderAttribute } = props.config;
        const CardNumber = props.data.loyaltyCard.result?.CardNumber;
        const tierID = tierId?.toLowerCase();
        return (
            <div className={dynamicClass}>
                <div className='loyalty-signup-custom-container'>
                    <Module {...LoyaltySignUp}>
                        <div className='ms-content-block'>
                            <div className='ms-content-block__details'>
                                {displayHeading(props)}
                                <div className='ms-content-block__text'>
                                    {tierID === 'club' ? <p>{memberStatement} </p> : <p>{nonClubMemberStatement} </p>}
                                </div>
                            </div>
                        </div>
                        <div className='loyalty-signup-section'>
                            {renderMainHeading(props)}
                            {!CardNumber && (
                                <>
                                    {renderSelectElement(titleLabel, updateTitle, pleaseSelectText, titleAttribute)}
                                    {titleNotSelectedErrorMsg()}
                                    {renderFirstAndLastName(props)}
                                    {renderMobileField()}
                                    {renderDateOfBirthField()}
                                </>
                            )}
                            {props.slots.address}
                            {addressError !== '' && <Node className='localstore-not-selected-error'>{addressError}</Node>}
                            {!CardNumber && (
                                <>
                                    {renderSelectElement(genderLabel, updateGenderValue, pleaseSelectText, genderAttribute)}
                                    {_genderNotSelectedErrorMsg()}
                                    {renderLocalStoreField()}
                                </>
                            )}
                            {renderPromoCodeField()}
                        </div>
                        {renderMarketingConsentSection()}
                        {renderLoyaltyTermsAndConditionsSection()}
                    </Module>
                </div>
            </div>
        );
    };
    // Due to use of useState hooks inside this function need to disable tslint
    // tslint:disable-next-line: max-func-body-length
    const renderJSX = (): JSX.Element => {
        const { redirectingLoaderText, authenticationMessage } = props.resources;
        let { clubFreeTierText, clubPlusTierText } = props.config;
        clubFreeTierText = clubFreeTierText?.toLowerCase();
        clubPlusTierText = clubPlusTierText?.toLowerCase();
        const tierID = tierId?.toLowerCase();
        const queryParam = query?.tier?.toLowerCase();
        const CardNumber = props.data.loyaltyCard.result?.CardNumber;
        if (!props.context.request.user.isAuthenticated) {
            return <RenderErrorMessageWithLoader message={authenticationMessage} />;
        } else if (CardNumber && tierID === clubFreeTierText && queryParam === 'club-free') {
            return <RenderErrorMessageWithLoader message={redirectingLoaderText} />;
        } else if (CardNumber && tierID === clubPlusTierText && (queryParam === 'club-plus' || queryParam === 'club-plus-trial')) {
            return <RenderErrorMessageWithLoader message={redirectingLoaderText} />;
        } else if (CardNumber && tierID === clubPlusTierText && queryParam === 'club-free') {
            return <RenderErrorMessageWithLoader message={redirectingLoaderText} />;
        } else {
            return <>{renderLoyaltySignUpView()}</>;
        }
    };
    // Here's the main rendering
    return <>{renderJSX()}</>;
};
// Custom hook to get custom address count
const useGetAddressesCount = (props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>) => {
    const [addressCount, setAddressCount] = useState<Address[] | undefined>([]);
    useEffect(() => {
        setAddressCount(props.data.getCustomerAddress.result?.addresses);
    });
    return addressCount;
};

// Provide the TierId of user's loyalty
const getTierId = (loyaltyGroup: LoyaltyGroup | undefined) => {
    // highestLoyaltCardTier
    const highestActiveLoyaltyCardTier = loyaltyGroup && loyaltyGroup.HighestActiveLoyaltyCardTier;
    const loyaltyCardTiers = loyaltyGroup && loyaltyGroup.LoyaltyCardTiers;
    if (loyaltyCardTiers && loyaltyCardTiers?.length > 0) {
        for (const [_, loyaltyCardTier] of loyaltyCardTiers.entries()) {
            if (loyaltyCardTier.RecordId === highestActiveLoyaltyCardTier) {
                return loyaltyCardTier.TierId;
            }
        }
    }
    return;
};

const renderMainHeading = (props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>) => {
    if (!props.data.loyaltyCard.result?.CardNumber) {
        return (
            <>
                {renderHeading(props.config.memberHeading)}
                <div className='loyalty-signup-section__title'>
                    <h2 className='ms-loyalty-signup__heading'>{props.resources.additionalDetailsLabel}</h2>
                </div>
            </>
        );
    }
    return;
};
// this will return attribute which we need to for club plus members like (direct debit, promo code).
const getCustomAttribute = (
    props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>,
    attribute: string,
    value: string
): CustomerAttribute => {
    // const { directDebitAttribute, directDebitAttributeValue} = props.config;
    const defaultAttribute = props.data.attributeDefinitions.result?.find(attr => attr.Name?.toLowerCase() === attribute?.toLowerCase());
    return {
        ['AttributeValue']: {
            // @ts-ignore
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CommercePropertyValue',
            StringValue: value
        },
        ['@odata.type']: '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CustomerAttribute',
        ['ExtensionProperties@odata.type']: '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.CommerceProperty)',
        ['NameTranslations@odata.type']: '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.TextValueTranslation)',
        ['NameTranslations']: [],
        ['DataTypeValue']: defaultAttribute && defaultAttribute.DataTypeValue,
        ['KeyName']: defaultAttribute && defaultAttribute.Name,
        ['Name']: defaultAttribute && defaultAttribute.Name,
        ['RecordId']: defaultAttribute && defaultAttribute.RecordId,
        ['ExtensionProperties']: []
    };
};
// This function will render generic headings
const renderHeading = (heading: (IHeadingData & IHeadingData) | undefined) => {
    return heading?.text && <Msdyn365.Text className='ms-loyalty-signup__heading' tag={heading.tag} text={heading.text} />;
};
// Render Customer's first and last name
const renderFirstAndLastName = (props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>) => {
    const {
        customerInformation: { result }
    } = props.data;
    return (
        <div className='loyalty-signup-section__personal-information'>
            <div className='loyalty-signup-section__personal-information--first-name'>
                <span>{props.resources.firstNameLabel}</span>
                <span>{result?.FirstName}</span>
            </div>
            <div className='loyalty-signup-section__personal-information--last-name'>
                <span>{props.resources.lastNameLabel}</span>
                <span>{result?.LastName}</span>
            </div>
        </div>
    );
};
// Function for setting attribute default value
const setAttrDefaultValues = (attribute: CustomerAttribute) => {
    return {
        // @ts-ignore
        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CustomerAttribute',
        Attribute: undefined,
        DataTypeValue: attribute.DataTypeValue,
        KeyName: attribute.Name,
        Name: attribute.Name,
        RecordId: attribute.RecordId,
        Units: undefined,
        AttributeValue: {
            // @ts-ignore
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CommercePropertyValue',
            StringValue: attribute.DataTypeValue === 5 ? '' : null,
            BooleanValue: attribute.DataTypeValue === 6 ? false : null
        },
        'ExtensionProperties@odata.type': '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.CommerceProperty)',
        ExtensionProperties: [],
        'NameTranslations@odata.type': '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.TextValueTranslation)',
        NameTranslations: []
    };
};

const setAttributeValue = (value: string | boolean, attribute: CustomerAttribute, isBoolean: boolean) => {
    return {
        ['AttributeValue']: {
            // @ts-ignore
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CommercePropertyValue',
            IntegerValue: null,
            BooleanValue: isBoolean ? value : null,
            StringValue: isBoolean ? null : value,
            DecimalValue: null
        },
        ['@odata.type']: '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CustomerAttribute',
        ['ExtensionProperties@odata.type']: '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.CommerceProperty)',
        ['NameTranslations@odata.type']: '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.TextValueTranslation)',
        ['NameTranslations']: [],
        ['DataTypeValue']: (attribute && attribute.DataTypeValue) || '',
        ['KeyName']: (attribute && attribute.Name) || '',
        ['Name']: (attribute && attribute.Name) || '',
        ['RecordId']: (attribute && attribute.RecordId) || '',
        ['ExtensionProperties']: []
    };
};
// Will render generic loader with given message
const RenderErrorMessageWithLoader: React.FC<{ message: string }> = ({ message }) => {
    return (
        <div className='loading-message-container'>
            <div className='loading-message-container__innerbox'>
                <Waiting />
                <span>{message}</span>
            </div>
        </div>
    );
};
// Redirects the user based on certain criteria defined inside function
const redirectUserIfTierExist = (props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>, tierId: string | undefined) => {
    const { clubFreeTierText, clubPlusTierText } = props.config;
    const {
        data: { loyaltyCard },
        context: {
            request: { query }
        }
    } = props;
    // -1 If user is already dobbies club free member and wants to enroll for it again
    // redirects him to loyalty page
    if (
        loyaltyCard.result?.CardNumber &&
        tierId?.toLowerCase() === clubFreeTierText?.toLowerCase() &&
        query?.tier?.toLowerCase() === 'club-free'
    ) {
        // redirects him to loyalty page.
        window.location.assign(Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '');
        return;
    }
    /* -2 If user is already dobbies club plus member and wants to enroll for it again redirects him to loyalty page */
    if (
        loyaltyCard.result?.CardNumber &&
        tierId?.toLowerCase() === clubPlusTierText?.toLowerCase() &&
        query?.tier?.toLowerCase() === 'club-plus'
    ) {
        // redirects him to loyalty page.
        window.location.assign(Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '');
        return;
    }

    /* -2 If user is already dobbies club plus member and wants to enroll for it again redirects him to loyalty page */
    if (
        loyaltyCard.result?.CardNumber &&
        tierId?.toLowerCase() === clubPlusTierText?.toLowerCase() &&
        query?.tier?.toLowerCase() === 'club-plus'
    ) {
        // redirects him to loyalty page.
        window.location.assign(Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '');
        return;
    }
    /* -3 If user is club plus member now he mistakenly wants to enroll in free tier redirects him to loyalty page */
    if (
        loyaltyCard.result?.CardNumber &&
        tierId?.toLowerCase() === clubPlusTierText?.toLowerCase() &&
        query?.tier?.toLowerCase() === 'club-free'
    ) {
        // redirects him to loyalty page.
        window.location.assign(Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '');
        return;
    }

    /* -4 If user is already dobbies club plus member and wants to enroll in dobbies free club trial redirects him to loyalty page */
    if (
        loyaltyCard.result?.CardNumber &&
        tierId?.toLowerCase() === clubPlusTierText?.toLowerCase() &&
        query?.tier?.toLowerCase() === 'club-plus'
    ) {
        // redirects him to loyalty page.
        window.location.assign(Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '');
        return;
    }
};

const _invalidDobErrorMsg = (
    displayNotification: boolean,
    error: string,
    dateError: string,
    isValidDob: Boolean,
    overEighteen: boolean,
    resources: ILoyaltySignUpResources,
    isYearOutOfRange: boolean,
    minYear: number,
    maxYear: number
) => {
    const { validDateErrorText, ageRestrictionError, validYearErrorText } = resources;
    const yearRangeErrorText = validYearErrorText.replace('{minYear}', minYear.toString()).replace('{maxYear}', maxYear.toString());
    if ((error !== '' || dateError !== '') && displayNotification && !isYearOutOfRange) {
        return (
            <Node className='invalid-input-dob-error'>
                {!isValidDob ? validDateErrorText : !overEighteen ? ageRestrictionError : undefined}
            </Node>
        );
    }
    if (error !== '' && isValidDob && isYearOutOfRange) {
        return <Node className='loyalty-validation-error'>{yearRangeErrorText}</Node>;
    }
    return;
};

const _renderTermsAndConditionsText = (
    termsAndConditionLink: ITermsAndConditionsLinkData,
    termsAndConditionsText: string,
    linkClass: string,
    textClass: string
) => {
    const termsAndConditionLinkData: Msdyn365.ILinksData = {
        ariaLabel: termsAndConditionLink.ariaLabel || undefined,
        className: linkClass,
        linkText: termsAndConditionLink.linkText,
        linkUrl: termsAndConditionLink.linkUrl && termsAndConditionLink.linkUrl.destinationUrl
    };
    return (
        <Node tag='p' className={textClass}>
            {termsAndConditionsText}
            <Msdyn365.Link link={termsAndConditionLinkData} />
        </Node>
    );
};
// Display main heading on page to differentiate b/w Club and Club Plus page
const displayHeading = (props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>) => {
    const { joiningClubPlusText, joiningClubText, joiningClubPlusTrialText } = props.resources;
    const query = props.context.request.query;
    if (query?.tier?.toLowerCase() === 'club-free') {
        return <h2 className='ms-content-block__title'>{joiningClubText}</h2>;
    } else if (query?.tier?.toLowerCase() === 'club-plus-trial') {
        return <h2 className='ms-content-block__title'>{joiningClubPlusTrialText}</h2>;
    } else if (query?.tier?.toLowerCase() === 'club-plus') {
        return <h2 className='ms-content-block__title'>{joiningClubPlusText}</h2>;
    } else {
        return <h2 className='ms-content-block__title'>{joiningClubText}</h2>;
    }
};
// Will update state after getting maxYear and minYear from values coming from D365 for Year Attribute
// tslint:disable-next-line: max-line-length
const setMinAndMaxYear = (
    props: ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>,
    SetMinYear: React.Dispatch<React.SetStateAction<number>>,
    minYear: number,
    SetMaxYear: React.Dispatch<React.SetStateAction<number>>,
    maxYear: number
): void => {
    useEffect(() => {
        if (minYear === -1 && maxYear === -1) {
            const attributes = props.data.attributeDefinitions.result;
            const yearAttr = attributes?.find(x => x.Name?.toLowerCase() === 'year');
            let years: number[] | undefined = [];
            let _minYear;
            if (yearAttr) {
                years = yearAttr.EnumerationDetails?.map(enumeration => {
                    // tslint:disable-next-line: radix
                    return parseInt(enumeration.EnumerationValue!);
                });
                _minYear = years && Math.min.apply(null, years);
            }
            SetMinYear(_minYear ? _minYear : minYear);
            // Now calculate max year as 18 years back from current date
            const date = new Date();
            const maximumYear = date.getFullYear() - 18;
            SetMaxYear(maximumYear);
        }
    });
};
// Provide dynamic class based on query param for club and club plus
const getDynamicClass = (
    query: Msdyn365.IDictionary<string> | undefined,
    props: React.PropsWithChildren<ILoyaltySignUpViewProps & ILoyaltySignUpProps<ILoyaltySignUpData>>
) => {
    let dynamicClass: string = 'dobbies-club-free';
    let loyaltyTermsLinkText: string = '';
    const queryParam = query && query.tier && query.tier.toLowerCase();
    if (queryParam === 'club-free') {
        dynamicClass = 'dobbies-club-free';
        loyaltyTermsLinkText = props.resources.loyaltyTermsLinkText;
    } else if (queryParam === 'club-plus' || queryParam === 'club-plus-trial') {
        dynamicClass = 'dobbies-club-plus club-signup-bg';
        loyaltyTermsLinkText = props.resources.loyaltyPlusTermsLinkText;
    }
    return { loyaltyTermsLinkText, dynamicClass };
};
export default LoyaltySignUpView;
